import './App.css';
import { Component, createRef } from 'react';
import GoogleLoginComponent from './GoogleLogin.js';
import { instanceOf } from 'prop-types';
import { withCookies, Cookies } from 'react-cookie';
import logo from './assets/logo.svg';
import exitIcon from './assets/exit_icon.svg';
import VideosList from './VideosList.js';
import ListOfThumbnails from './ListOfThumbnails.js';

import { BrowserRouter as Router, Route, Routes, Link } from 'react-router-dom'; // Import routing components
import PrivacyPolicy from './PrivacyPolicy'; // Import new components
import TermsOfService from './TermsOfService';

import { createTheme } from '@mui/material/styles';

const backend = process.env.REACT_APP_BACKEND || 'localhost:9000';

// eslint-disable-next-line
const scrollbarTheme = createTheme({
  overrides: {
    MuiCssBaseline: {
      '@global': {
        body: {
          scrollbarColor: '#6b6b6b #2b2b2b',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            backgroundColor: '#2b2b2b',
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: '#6b6b6b',
            minHeight: 24,
            border: '3px solid #2b2b2b',
          },
          '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus':
            {
              backgroundColor: '#959595',
            },
          '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active':
            {
              backgroundColor: '#959595',
            },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover':
            {
              backgroundColor: '#959595',
            },
          '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner': {
            backgroundColor: '#2b2b2b',
          },
        },
      },
    },
  },
});

class App extends Component {
  static propTypes = {
    cookies: instanceOf(Cookies).isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      selectedVideoIndex: null,
      selectedVideoObject: null,
      selectedVideoArchiveValue: null,
    };
    this.videosListRef = createRef();
  }

  componentDidMount() {
    let urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (code) {
      console.log('new cookie found: ' + code);
      this.props.cookies.set('code', code, { path: '/' });

      let url = new URL(window.location);
      url.searchParams.delete('code');
      window.history.pushState({}, '', url);
      this.forceUpdate();
    }
  }

  handleVideoSelect = (index, videoObject, archiveValue) => {
    if (index === null) {
      this.setState({
        selectedVideoIndex: null,
        selectedVideoObject: null,
        selectedVideoArchiveValue: null,
      });
    } else {
      this.setState({
        selectedVideoIndex: index,
        selectedVideoObject: videoObject,
        selectedVideoArchiveValue: archiveValue,
      });
    }
  };

  handleLogout = () => {
    this.props.cookies.remove('code', { path: '/' });
    this.setState({
      selectedVideoIndex: null,
      selectedVideoObject: null,
      selectedVideoArchiveValue: null,
    });
  };

  refreshAllVideoData = () => {
    if (this.videosListRef.current) {
      if (this.videosListRef.current.loadStatuses) {
        this.videosListRef.current.loadStatuses();
      }
      if (this.videosListRef.current.loadArchivedRotations) {
         this.videosListRef.current.loadArchivedRotations();
      }
    }
  };

  render() {
    const isLoggedIn = !!this.props.cookies.get('code');

    // Render different content based on URL path and login status
    return (
      <Router>
        <Routes>
          {/* Route for Privacy Policy Page */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />

          {/* Route for Terms of Service Page */}
          <Route path="/terms-of-service" element={<TermsOfService />} />

          {/* Main application route (landing page or logged-in view) */}
          <Route
            path="/"
            element={
              !isLoggedIn ? (
                <div className="App container-fluid">
                  <header className="app-header-logged-in" style={{ backgroundColor: 'transparent', borderBottom: 'none' }}>
                    <div className="logo-section"></div>
                    <div className="header-actions">
                      <a href="https://docs.google.com/forms/d/e/1FAIpQLSeCcB3-ebwz068IDwS9nhevMGgzd6hjh1lq3X9HSrKQIXuCQg/viewform?usp=dialog" target="_blank" rel="noopener noreferrer" className="feedback-button">
                        Feedback
                      </a>
                    </div>
                  </header>
                  <div className="landing-page" style={{ paddingTop: '10px' }}>
                    <img src={logo} className="landing-logo" alt="logo" />
                    <h1 className="landing-title">A/B Testing for YouTube</h1>
                    <p className="landing-subtitle">
                      Optimize your video thumbnails, increase CTR, and grow your channel with data-driven decisions.
                    </p>

                    <div className="features-grid">
                      <div className="feature-card">
                        <div className="feature-icon">🔄</div>
                        <h3 className="feature-title">Automated Thumbnail Rotation</h3>
                        <p className="feature-description">Upload multiple thumbnail variants for your videos. We automatically rotate them on YouTube at your specified intervals to gather performance data.</p>
                      </div>
                      <div className="feature-card">
                        <div className="feature-icon">📊</div>
                        <h3 className="feature-title">Detailed Analytics</h3>
                        <p className="feature-description">Track views, watch time, and click-through rates for each variant. Our dashboard makes it easy to compare performance and identify the winner.</p>
                      </div>
                      <div className="feature-card">
                        <div className="feature-icon">🚀</div>
                        <h3 className="feature-title">Boost Your Growth</h3>
                        <p className="feature-description">Stop guessing which thumbnail works best. Use real audience data to maximize your video's potential and reach more viewers.</p>
                      </div>
                    </div>

                    <div className="cta-section">
                      <h2 className="cta-title">Ready to optimize your content?</h2>
                      <p className="cta-text">Sign in with your Google account to get started.</p>
                      <GoogleLoginComponent backend={backend} showLoginOnly={true} />
                    </div>

                    {/* Footer for policy links on landing page */}
                    <div className="landing-page-footer">
                      <Link to="/privacy-policy" className="footer-link">Privacy Policy</Link>
                      <span>|</span>
                      <Link to="/terms-of-service" className="footer-link">Terms of Service</Link>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="App logged-in-view container-fluid">
                  <header className="app-header-logged-in">
                    <div className="logo-section">
                      <img src={logo} className="App-logo-header" alt="logo" />
                      <span>A/B testing</span>
                    </div>
                    <div className="header-actions">
                      <a href="https://docs.google.com/forms/d/e/1FAIpQLSeCcB3-ebwz068IDwS9nhevMGgzd6hjh1lq3X9HSrKQIXuCQg/viewform?usp=dialog" target="_blank" rel="noopener noreferrer" className="feedback-button">
                        Feedback
                      </a>
                      <button onClick={this.handleLogout} className="sign-out-button">
                        <img src={exitIcon} alt="Sign Out" className="sign-out-icon" />
                        Sign Out
                      </button>
                    </div>
                  </header>

                  <main className="main-content">
                    <div className={this.state.selectedVideoObject !== null ? "videos-list-container-split" : "videos-list-container-full"}>
                      <VideosList
                        ref={this.videosListRef}
                        backend={backend}
                        onVideoSelect={this.handleVideoSelect}
                        selectedVideoIndex={this.state.selectedVideoIndex}
                        cookies={this.props.cookies}
                        refreshAllVideoData={this.refreshAllVideoData}
                      />
                    </div>
                    {this.state.selectedVideoObject !== null && (
                      <div className="video-details-container">
                        <ListOfThumbnails
                          cookies={this.props.cookies}
                          selectedVideo={this.state.selectedVideoObject}
                          video_ID={this.state.selectedVideoObject.video_ID}
                          thumbnail={this.state.selectedVideoObject.thumbnail}
                          archiveValue={this.state.selectedVideoArchiveValue}
                          loadStatuses={this.refreshAllVideoData}
                          backend={backend}
                          refreshAllVideoData={this.refreshAllVideoData}
                        />
                      </div>
                    )}
                  </main>
                </div>
              )
            }
          />
        </Routes>
      </Router>
    );
  }
}

export default withCookies(App);
